"use client";

/**
 * Stale-chunk recovery.
 *
 * A deploy replaces every hashed file under `/_next/static`. A visitor who
 * had a page open across that deploy still holds the old route manifest, so
 * their next navigation requests a chunk that no longer exists, the lazy
 * import rejects, and React unwinds to the nearest error boundary.
 *
 * This is the most common client-side failure Rulebound actually has:
 * Application Insights recorded fourteen `ChunkLoadError`s across thirteen
 * distinct pages between 2026-08-04 and 2026-08-14 — `/explore`, `/packs`,
 * `/terms`, `/authors/*` and several book pages — most of them landing on the
 * *root* boundary, which replaces the entire app with a crash screen.
 *
 * The crash screen's "Try again" cannot fix it: retrying re-renders the same
 * doomed import against the same stale manifest. A full reload fetches the
 * current manifest and the navigation simply works. So reload instead.
 *
 * Loop guard: a timestamp in sessionStorage. If a reload lands on another
 * chunk error inside the window below, the deploy is genuinely broken and an
 * honest error card beats an infinite refresh. The stamp expires on its own,
 * so a stale-chunk error a week later still gets its own single retry —
 * nothing has to remember to clear it.
 */

const RELOAD_STAMP = "rb:chunk-reload";
const RETRY_WINDOW_MS = 30_000;

/**
 * Chunk failures don't share one error class across browsers and bundlers:
 * webpack throws a named `ChunkLoadError`, native ESM imports reject with a
 * plain TypeError whose wording differs per engine. Match on both.
 */
export function isChunkLoadError(error: unknown): boolean {
  if (!error || typeof error !== "object") return false;
  if ((error as { name?: unknown }).name === "ChunkLoadError") return true;
  const message = (error as { message?: unknown }).message;
  return (
    typeof message === "string" &&
    /chunkloaderror|loading chunk|failed to load chunk|error loading dynamically imported module|importing a module script failed/i.test(
      message,
    )
  );
}

/**
 * Reload once to pick up the current deploy's chunks.
 *
 * Returns true when a reload was scheduled — the caller should leave its
 * fallback UI unrendered, because the page is on its way out.
 */
export function recoverFromChunkError(error: unknown): boolean {
  if (!isChunkLoadError(error)) return false;

  try {
    const previous = Number(sessionStorage.getItem(RELOAD_STAMP));
    if (previous && Date.now() - previous < RETRY_WINDOW_MS) return false;
    sessionStorage.setItem(RELOAD_STAMP, String(Date.now()));
  } catch {
    // Without sessionStorage there is no loop guard, and an unbounded reload
    // loop is worse than the crash screen. Show the fallback instead.
    return false;
  }

  // A beat before navigating, so the telemetry the boundary just queued has
  // a chance to leave the page. Short enough that nobody sees a stuck frame.
  window.setTimeout(() => window.location.reload(), 200);
  return true;
}
